import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './UseCasePages.css';
import blueShieldImg from '../../assets/blueshield.png';

const UseCaseMedical = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['uc-hero', 'section-overview', 'section-pii-phi', 'section-sensitive-data', 'section-tool-governance', 'section-compliance', 'section-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const title = el.querySelector('h2, p');
      const target = title || el;
      const nav = document.querySelector('.nav-container');
      const navH = nav ? nav.offsetHeight : 64;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - navH - 40, behavior: 'smooth' });
    }
  };

  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, p');
      const target = title || el;
      return { scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>

      <section id="uc-hero" className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Medical &amp; Healthcare</h1>
      </section>

      <section className="ucp-content">
        <div className="ucp-layout">
          <h2 className="ucp-heading">AI Governance for Healthcare</h2>
          <p className="ucp-description">
            Automate AI governance for PII, PHI, and sensitive health information with Bltz Workspace — enforcing data protection policies, managing tool connection governance rules, and ensuring HIPAA compliance across every AI system in your healthcare organization.
          </p>

          <div className="ucp-cta" style={{ marginTop: 0, marginBottom: 20 }}>
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* 4 Feature Boxes */}
          <div className="ucp-features">
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">PII &amp; PHI Protection</h3>
              </div>
              <p className="ucp-feature-desc">Automatically detect and prevent exposure of patient data, medical records, and health identifiers in AI interactions.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-pii-phi')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">Sensitive Data Governance</h3>
              </div>
              <p className="ucp-feature-desc">Define and enforce rules for how AI systems handle diagnoses, prescriptions, and treatment information.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-sensitive-data')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">Tool Connection Rules</h3>
              </div>
              <p className="ucp-feature-desc">Govern which AI tools can access EHR systems, lab results, and clinical databases with granular controls.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-tool-governance')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="ucp-feature-box">
              <div className="ucp-feature-header">
                <h3 className="ucp-feature-title">HIPAA Compliance</h3>
              </div>
              <p className="ucp-feature-desc">Automated HIPAA compliance monitoring and audit-ready reporting for all AI-powered healthcare workflows.</p>
              <div className="ucp-feature-arrow" onClick={() => scrollToId('section-compliance')}>
                <span>&#8595;</span>
              </div>
            </div>
          </div>

          {/* Overview Section */}
          <div id="section-overview" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">The Challenge: AI in Healthcare</h2>
            <p className="ucp-section-desc">
              Healthcare organizations are deploying AI for clinical decision support, patient communication, medical coding, and administrative automation. These systems process the most sensitive categories of personal data — Protected Health Information (PHI), diagnoses, treatment plans, and genetic data. Without automated governance, organizations face HIPAA violations, patient privacy breaches, and uncontrolled AI tool proliferation across departments.
            </p>
            <div className="ucp-diagram">
              <div className="ucp-diagram-flow">
                <div className="ucp-diagram-node ucp-node-threat">
                  <div className="ucp-node-icon">&#127973;</div>
                  <span>Healthcare AI Tools</span>
                </div>
                <div className="ucp-diagram-arrow">&rarr;</div>
                <div className="ucp-diagram-node ucp-node-shield">
                  <img src={blueShieldImg} alt="Bltz Workspace" className="ucp-node-logo" />
                  <span>Bltz Workspace</span>
                </div>
                <div className="ucp-diagram-arrow">&rarr;</div>
                <div className="ucp-diagram-node ucp-node-safe">
                  <div className="ucp-node-icon">&#10003;</div>
                  <span>Governed &amp; Compliant</span>
                </div>
              </div>
            </div>
          </div>

          {/* PII/PHI Section */}
          <div id="section-pii-phi" className="ucp-section">
            <h2 className="ucp-section-title">PII &amp; PHI Protection</h2>
            <p className="ucp-section-desc">
              Bltz Workspace automatically identifies and governs all 18 HIPAA identifiers — patient names, medical record numbers, Social Security numbers, dates of service, and more — across every AI interaction. Governance policies run in real time, detecting when AI tools attempt to process, store, or transmit PHI without proper authorization.
            </p>
            <div className="ucp-data-grid">
              <div className="ucp-data-category">
                <h4 className="ucp-data-cat-title">Patient Identifiers (PHI)</h4>
                <div className="ucp-data-tags">
                  <span className="ucp-data-tag ucp-tag-phi">Patient Name</span>
                  <span className="ucp-data-tag ucp-tag-phi">Medical Record #</span>
                  <span className="ucp-data-tag ucp-tag-phi">Date of Birth</span>
                  <span className="ucp-data-tag ucp-tag-phi">SSN</span>
                  <span className="ucp-data-tag ucp-tag-phi">Health Plan ID</span>
                  <span className="ucp-data-tag ucp-tag-phi">Biometric Data</span>
                </div>
              </div>
              <div className="ucp-data-category">
                <h4 className="ucp-data-cat-title">Clinical Data</h4>
                <div className="ucp-data-tags">
                  <span className="ucp-data-tag ucp-tag-clinical">Diagnoses</span>
                  <span className="ucp-data-tag ucp-tag-clinical">Prescriptions</span>
                  <span className="ucp-data-tag ucp-tag-clinical">Lab Results</span>
                  <span className="ucp-data-tag ucp-tag-clinical">Treatment Plans</span>
                  <span className="ucp-data-tag ucp-tag-clinical">Imaging Reports</span>
                  <span className="ucp-data-tag ucp-tag-clinical">Genetic Data</span>
                </div>
              </div>
              <div className="ucp-data-category">
                <h4 className="ucp-data-cat-title">Governance Actions</h4>
                <div className="ucp-data-tags">
                  <span className="ucp-data-tag ucp-tag-action">Auto-Redact</span>
                  <span className="ucp-data-tag ucp-tag-action">Block Transmission</span>
                  <span className="ucp-data-tag ucp-tag-action">Alert &amp; Log</span>
                  <span className="ucp-data-tag ucp-tag-action">Quarantine</span>
                  <span className="ucp-data-tag ucp-tag-action">Mask in Output</span>
                  <span className="ucp-data-tag ucp-tag-action">Audit Trail</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sensitive Data Section */}
          <div id="section-sensitive-data" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">Sensitive Information Governance</h2>
            <p className="ucp-section-desc">
              Beyond standard PHI, healthcare AI systems process sensitive information categories that require specialized governance — mental health records, substance abuse treatment data, reproductive health information, and HIV/AIDS status. Bltz Workspace enforces granular policies for each data category, ensuring stricter-than-HIPAA protections where required by state and federal regulations.
            </p>
            <div className="ucp-checklist">
              <div className="ucp-check-item">
                <img src={blueShieldImg} alt="" className="ucp-check-logo" />
                <div>
                  <h4>Mental Health Records</h4>
                  <p>42 CFR Part 2 compliant governance — substance abuse and mental health treatment records receive enhanced protections beyond standard HIPAA requirements.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <img src={blueShieldImg} alt="" className="ucp-check-logo" />
                <div>
                  <h4>Genetic Information</h4>
                  <p>GINA-compliant protections prevent AI systems from processing or exposing genetic information for unauthorized purposes including insurance and employment decisions.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <img src={blueShieldImg} alt="" className="ucp-check-logo" />
                <div>
                  <h4>Minor Patient Data</h4>
                  <p>COPPA and state-specific minor consent laws enforced — AI interactions involving pediatric patients receive age-appropriate data handling rules.</p>
                </div>
              </div>
              <div className="ucp-check-item">
                <img src={blueShieldImg} alt="" className="ucp-check-logo" />
                <div>
                  <h4>Research &amp; Clinical Trial Data</h4>
                  <p>IRB protocol alignment — ensure AI systems handling research data maintain de-identification standards and consent boundaries.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tool Connection Governance Section */}
          <div id="section-tool-governance" className="ucp-section">
            <h2 className="ucp-section-title">Tool Connection Governance Rules</h2>
            <p className="ucp-section-desc">
              Healthcare organizations use dozens of AI-powered tools that connect to EHR systems, lab information systems, PACS imaging, and pharmacy databases. Bltz Workspace provides centralized governance over which AI tools can access which systems, with granular connection rules that prevent unauthorized data flows and maintain the minimum necessary standard.
            </p>
            <div className="ucp-tool-grid">
              <div className="ucp-tool-card">
                <div className="ucp-tool-header">
                  <span className="ucp-tool-status ucp-status-governed">Governed</span>
                  <h4>EHR System Access</h4>
                </div>
                <p>Only authorized clinical AI tools may read patient records. Write access requires additional approval and real-time audit logging.</p>
                <div className="ucp-tool-rules">
                  <span className="ucp-tool-rule">Read: Clinical AI Only</span>
                  <span className="ucp-tool-rule">Write: Approved + Audited</span>
                  <span className="ucp-tool-rule">Export: Blocked</span>
                </div>
              </div>
              <div className="ucp-tool-card">
                <div className="ucp-tool-header">
                  <span className="ucp-tool-status ucp-status-governed">Governed</span>
                  <h4>Lab &amp; Imaging Systems</h4>
                </div>
                <p>AI diagnostic tools access lab results and imaging with role-based permissions. Bulk data extraction is prohibited without explicit authorization.</p>
                <div className="ucp-tool-rules">
                  <span className="ucp-tool-rule">Read: Role-Based</span>
                  <span className="ucp-tool-rule">Bulk Export: Prohibited</span>
                  <span className="ucp-tool-rule">Results: Anonymized</span>
                </div>
              </div>
              <div className="ucp-tool-card">
                <div className="ucp-tool-header">
                  <span className="ucp-tool-status ucp-status-governed">Governed</span>
                  <h4>Pharmacy &amp; Rx Systems</h4>
                </div>
                <p>Medication AI tools operate under strict DEA and state pharmacy board rules. Controlled substance data requires enhanced authentication.</p>
                <div className="ucp-tool-rules">
                  <span className="ucp-tool-rule">DEA Schedule: Enhanced Auth</span>
                  <span className="ucp-tool-rule">Rx Data: Encrypted Transit</span>
                  <span className="ucp-tool-rule">Access: Time-Limited</span>
                </div>
              </div>
              <div className="ucp-tool-card">
                <div className="ucp-tool-header">
                  <span className="ucp-tool-status ucp-status-blocked">Blocked</span>
                  <h4>Unsanctioned AI Tools</h4>
                </div>
                <p>Shadow AI tools without proper BAA agreements and security reviews are automatically blocked from accessing any healthcare data systems.</p>
                <div className="ucp-tool-rules">
                  <span className="ucp-tool-rule">No BAA: Auto-Blocked</span>
                  <span className="ucp-tool-rule">Unreviewed: Quarantined</span>
                  <span className="ucp-tool-rule">Alert: Security Team</span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Section */}
          <div id="section-compliance" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">Healthcare Compliance Automation</h2>
            <p className="ucp-section-desc">
              Bltz Workspace automates compliance monitoring and evidence collection across HIPAA, HITECH, and state-specific healthcare privacy regulations — providing continuous assurance that AI operations meet regulatory requirements.
            </p>
            <div className="ucp-compliance-grid">
              <div className="ucp-compliance-card">
                <h4>HIPAA</h4>
                <p>Automated enforcement of Privacy Rule, Security Rule, and Breach Notification requirements across all AI systems handling PHI.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>HITECH</h4>
                <p>Enhanced breach notification compliance and increased penalties enforcement — AI systems monitored for unauthorized PHI access and disclosure.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>FDA AI/ML</h4>
                <p>Governance for AI/ML-based Software as Medical Device (SaMD) — monitoring model changes, performance drift, and safety updates.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>State Privacy Laws</h4>
                <p>Automated compliance with state-specific health privacy regulations including CCPA health data provisions and state biometric laws.</p>
              </div>
            </div>
          </div>

          {/* Metrics Section */}
          <div className="ucp-section">
            <h2 className="ucp-section-title">Governance at Scale</h2>
            <div className="ucp-metrics">
              <div className="ucp-metric">
                <span className="ucp-metric-value">18</span>
                <span className="ucp-metric-label">HIPAA Identifiers Monitored</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">100%</span>
                <span className="ucp-metric-label">AI Tool Connection Visibility</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">Real-Time</span>
                <span className="ucp-metric-label">PHI Detection &amp; Redaction</span>
              </div>
              <div className="ucp-metric">
                <span className="ucp-metric-value">24/7</span>
                <span className="ucp-metric-label">Compliance Monitoring</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div id="section-cta" className="ucp-cta">
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See Healthcare Governance in Action &rarr;
            </button>
          </div>

          <span className="ucp-back-link" onClick={() => { navigate('/use-cases'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            ← Back to Use Cases
          </span>
        </div>
      </section>
    </>
  );
};

export default UseCaseMedical;
